import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Home, ChevronRight, UtensilsCrossed } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase, getImageUrl } from '../lib/supabase';
import { HERO_IMAGE } from '../lib/constants';
import { useFeaturedProducts } from '../services/productService';
import { DesktopFilterSidebar, MobileFilterBar, MobileFilterPanel } from '../components/FilterBar/FilterBar';
import { ProductGrid } from '../components/ProductGrid/ProductGrid';

const ROTATE_INTERVAL = 5000;

/**
 * Menu page — hero banner with auto-rotating product images,
 * then sidebar filters + scrollable product list below.
 */
export default function MenuPage() {
  const queryClient = useQueryClient();
  const { data: featuredProducts } = useFeaturedProducts();
  const [heroIdx, setHeroIdx] = useState(0);

  // Build hero images from featured products
  const heroImages = (featuredProducts || [])
    .filter((p) => p.image_path)
    .map((p) => getImageUrl(p.image_path));

  // Fallback to static hero image
  const displayImages = heroImages.length > 0 ? heroImages : [HERO_IMAGE];

  // Auto-rotate hero images
  useEffect(() => {
    if (displayImages.length <= 1) return;
    const timer = setInterval(() => {
      setHeroIdx((i) => (i + 1) % displayImages.length);
    }, ROTATE_INTERVAL);
    return () => clearInterval(timer);
  }, [displayImages.length]);

  useEffect(() => {
    const channel = supabase
      .channel('products-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'products' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['products'] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'categories' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['categories'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return (
    <main className="min-h-screen bg-ui-background flex flex-col">
      {/* ── Hero area with breadcrumbs ── */}
      <section className="relative min-h-[28vh] sm:min-h-[32vh] lg:min-h-[36vh] flex items-end overflow-hidden">
        {/* Background image — auto-rotates through featured products */}
        <div className="absolute inset-0">
          <AnimatePresence mode="wait">
            <motion.img
              key={heroIdx}
              src={displayImages[heroIdx % displayImages.length]}
              alt="Menu background"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 via-brand-dark/40 to-brand-dark/20" />
        </div>

        {/* Content overlay */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-5 sm:pb-6">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-1.5 text-sm mb-3">
            <Link
              to="/"
              className="flex items-center gap-1 text-brand-white/70 hover:text-brand-white transition-colors"
            >
              <Home size={14} />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <ChevronRight size={14} className="text-brand-white/40" />
            <span className="text-brand-white font-medium">Menu</span>
          </nav>

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <h1 className="flex items-center gap-2.5 text-2xl sm:text-3xl lg:text-4xl font-extrabold text-brand-white drop-shadow-lg">
              <UtensilsCrossed size={24} className="text-brand-white/90" />
              Our Menu
            </h1>
            <p className="text-sm sm:text-base text-brand-white/70 mt-1">
              Browse our selection of handcrafted meals
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mobile sticky filter bar */}
      <MobileFilterBar />

      {/* ── Sidebar + results ── */}
      <div className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <div className="lg:flex lg:gap-6 lg:items-start h-full">
          <DesktopFilterSidebar />
          <div className="flex-1 min-w-0">
            <ProductGrid />
          </div>
        </div>
      </div>

      <MobileFilterPanel />
    </main>
  );
}
